<!-- BaseButton.vue - Universal button component -->
<template>
  <button
    :type="buttonType"
    :class="buttonClasses"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <!-- Loading Spinner -->
    <span v-if="loading" class="btn-spinner">
      <span class="spinner"></span>
    </span>

    <!-- Icon Top -->
    <span v-if="icon && iconPosition === 'top' && !loading" class="btn-icon btn-icon--top">
      <component :is="icon" v-if="typeof icon === 'object'" />
      <i v-else :class="icon"></i>
    </span>

    <!-- Icon Left -->
    <span v-if="icon && iconPosition === 'left' && !loading" class="btn-icon btn-icon--left">
      <component :is="icon" v-if="typeof icon === 'object'" />
      <i v-else :class="icon"></i>
    </span>

    <!-- Button Text -->
    <span class="btn-text">
      <slot>{{ text }}</slot>
    </span>

    <!-- Icon Right -->
    <span v-if="icon && iconPosition === 'right' && !loading" class="btn-icon btn-icon--right">
      <component :is="icon" v-if="typeof icon === 'object'" />
      <i v-else :class="icon"></i>
    </span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',

  // Declaring emits prevents @click from falling through $attrs to the root element.
  // Without this, clicking the button fires the parent's @click handler TWICE:
  // once via native click ($attrs fallthrough) and once via this.$emit('click').
  emits: ['click'],

  props: {
    /**
     * Button text content
     */
    text: {
      type: String,
      default: ''
    },

    /**
     * Button variant/type
     * @values primary, secondary, outline, outline-yellow, text, danger
     */
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'outline-yellow', 'text', 'danger'].includes(value)
    },

    /**
     * Button size
     * @values small, medium, large
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },

    /**
     * Icon (can be string class or Vue component)
     */
    icon: {
      type: [String, Object],
      default: null
    },

    /**
     * Icon position
     * @values left, right, top
     */
    iconPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right', 'top'].includes(value)
    },

    /**
     * HTML button type
     * @values button, submit, reset
     */
    buttonType: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },

    /**
     * Disabled state
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Loading state
     */
    loading: {
      type: Boolean,
      default: false
    },

    /**
     * Full width button
     */
    block: {
      type: Boolean,
      default: false
    },

    /**
     * Custom color scheme (overrides variant)
     */
    color: {
      type: String,
      default: null
    }
  },

  computed: {
    buttonClasses() {
      return [
        'base-button',
        `base-button--${this.variant}`,
        `base-button--${this.size}`,
        {
          'base-button--block': this.block,
          'base-button--disabled': this.disabled,
          'base-button--loading': this.loading,
          'base-button--icon-only': this.icon && !this.text && !this.$slots.default,
          'base-button--with-icon': this.icon,
          'base-button--icon-top': this.icon && this.iconPosition === 'top'
        }
      ];
    }
  },

  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;

// Base button styles
.base-button {
  // Layout
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  flex-direction: row; // Default: horizontal layout
  
  // Icon top layout
  &.base-button--icon-top {
    flex-direction: column;
    gap: 10px; // ЗБІЛЬШЕНО - було 12px, зробимо 10px як у Figma
  }
  
  // Typography
  font-family: $font-family-base;
  font-weight: 400;
  text-align: center;
  line-height: 1.5;
  // white-space: nowrap; // ПРИБРАНО - дозволяємо перенос тексту
  
  // Interaction
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  text-decoration: none;
  
  // Default state
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px $shadow-brand;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
}

// Sizes
.base-button--small {
  padding: 12px 20px; // Збільшуємо ще більше
  font-size: 14px;
  line-height: 20px;
  border-radius: 4px;
  min-height: 36px;
}

.base-button--medium {
  padding: 7px 24px; // Збільшуємо ще більше
  font-size: 14px;
  line-height: 24px;
  border-radius: 5px;
  //max-height: 39px;
  //min-height: 39px;
}

.base-button--large {
  padding: 16px 28px; // Збільшуємо ще більше
  font-size: 16px;
  line-height: 24px;
  border-radius: 5px;
  min-height: 48px;
}

// Спеціальний padding для icon-top кнопок
.base-button--icon-top {
  padding: 16px 12px !important; // ЗМІНЕНО - більший padding для artwork method кнопок
  
  &.base-button--medium {
    padding: 16px 12px !important; // Однаковий padding
  }
}

// Variants
.base-button--primary {
  background: $brand-primary;
  color: $color-white;
  border-color: $brand-secondary;

  &:hover:not(:disabled) {
    background: $brand-primary-dark;
    border-color: $brand-secondary;
  }

  &:active:not(:disabled) {
    background: $brand-primary-dark;
  }
}

.base-button--secondary {
  background: $color-white;
  color: $brand-primary;
  border-color: $color-border;

  &:hover:not(:disabled) {
    background: $color-bg-light;
    border-color: $brand-primary;
  }

  &:active:not(:disabled) {
    background: $color-bg-light;
  }
}

.base-button--outline {
  background: transparent;
  color: $brand-primary;
  border-color: $brand-primary;

  &:hover:not(:disabled) {
    background: $shadow-brand;
    border-color: $brand-primary-dark;
  }

  &:active:not(:disabled) {
    background: $shadow-brand;
  }
}

// NEW: Outline Yellow variant (Request a Sample)
.base-button--outline-yellow {
  background: transparent;
  color: #DAA520; // Dark golden text
  border-color: #FAA633; // Goldenrod border
  border-width: 1px;
  display:none;//

  &:hover:not(:disabled) {
    background: rgba(218, 165, 32, 0.05); // Light golden background
    border-color: #B8860B;
    color: #8B6914; // Darker on hover
  }

  &:active:not(:disabled) {
    background: rgba(218, 165, 32, 0.1);
  }
}

.base-button--text {
  background: transparent;
  color: $brand-primary;
  border-color: transparent;
  padding-left: 8px;
  padding-right: 8px;

  &:hover:not(:disabled) {
    background: $shadow-brand;
  }

  &:active:not(:disabled) {
    background: $shadow-brand;
  }
}

.base-button--danger {
  background: $color-danger;
  color: $color-white;
  border-color: #A82829;

  &:hover:not(:disabled) {
    background: $color-danger-dark;
  }

  &:active:not(:disabled) {
    background: $color-danger-dark;
  }
}

// States
.base-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.base-button--loading {
  pointer-events: none;
  
  .btn-text,
  .btn-icon {
    opacity: 0.5;
  }
}

.base-button--block {
  width: 100%;
  display: flex;
}

.base-button--icon-only {
  padding-left: 12px;
  padding-right: 12px;
  
  &.base-button--small {
    min-width: 36px;
    padding: 8px;
  }
  
  &.base-button--medium {
    min-width: 44px;
    padding: 10px;
  }
  
  &.base-button--large {
    min-width: 48px;
    padding: 12px;
  }
}

// Icon
.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg,
  i {
    width: 20px;
    height: 20px;
    display: block;
  }
}

.btn-icon--left {
  margin-right: -4px;
}

.btn-icon--right {
  margin-left: -4px;
}

.btn-icon--top {
  margin-bottom: -4px;
  
  svg,
  i {
    width: 35px;
    height: 35px; // Larger icon for top position
  }
}

// Text
.btn-text {
  display: inline-block;
  white-space: normal; // ДОДАНО - дозволяємо перенос тексту
  text-align: center; // ДОДАНО - центруємо текст
}

// Для icon-top кнопок текст може бути багаторядковим
.base-button--icon-top .btn-text {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
  font-weight: 600;
}

// Loading spinner
.btn-spinner {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
